// src/pages/ClaimAdsPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FiCheckCircle, FiAlertTriangle, FiArrowLeft, FiCircle, } from 'react-icons/fi';
import { FaBuysellads } from "react-icons/fa6";
import { motion, AnimatePresence } from 'motion/react';
import Modal from '../../components/Modal/Modal';
import './ClaimAds.css';

const showToast = (...args) => window.showToast?.(...args);

const STEPS = {
    SEARCH: 'SEARCH',
    SELECT: 'SELECT',
    SUCCESS: 'SUCCESS'
};

const ClaimAdsPage = () => {
    const [step, setStep] = useState(STEPS.SEARCH);
    const [redditId, setRedditId] = useState('');
    const [foundAds, setFoundAds] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [confirmMsg, setConfirmMsg] = useState('');
    const navigate = useNavigate();

    // STEP 1: Find the ads to show the user
    const handleInitialSearch = async () => {
        if (!redditId.trim()) return;
        setLoading(true);

        try {
            const q = query(
                collection(db, 'listings'),
                where('redditUsername', '==', redditId.trim())
            );
            const snap = await getDocs(q);
            const ads = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (ads.length === 0) {
                showToast("No ads found for this Reddit username. Please check the spelling.", "warning");
            } else {
                setFoundAds(ads);
                setSelectedIds(ads.map(a => a.id)); // Default: Select all
                setStep(STEPS.SELECT);
            }
        } catch (error) {
            showToast("Error searching ads: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // STEP 2: Show confirmation modal before executing claim
    const handleFinalClaim = () => {
        const unselectedCount = foundAds.length - selectedIds.length;

        const msg = unselectedCount > 0
            ? `You have not selected ${unselectedCount} ads. These will be PERMANENTLY deleted. Are you sure?`
            : `Claim these ${selectedIds.length} ads to your account?`;

        setConfirmMsg(msg);
        setShowModal(true);
    };

    // STEP 3: Execute the selective claim via Cloud Function
    const executeClaim = async () => {
        setShowModal(false);
        setLoading(true);
        try {
            const functions = getFunctions();
            const claimFn = httpsCallable(functions, 'claimConciergeAds');

            const response = await claimFn({
                redditUsername: redditId.trim(),
                selectedAdIds: selectedIds // Passing the IDs the user wants to keep
            });

            setResult(response.data);
            setStep(STEPS.SUCCESS);
        } catch (error) {
            showToast("Error claiming ads: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const toggleAdSelection = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    return (
        <div className="claim-ads-page-container">
            <button className="back-to-profile" onClick={() => navigate('/profile')}>
                <FiArrowLeft /> Back to Profile
            </button>

            <div className="claim-page">
                <div className={`claim-card ${step === STEPS.SELECT ? 'wide' : ''}`}>
                    <AnimatePresence mode="wait">

                        {/* --- STEP: SEARCH --- */}
                        {step === STEPS.SEARCH && (
                            <motion.div
                                key="search"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <h1>Claim Your Reddit Ads</h1>
                                <p>Enter your Reddit username to find your concierge listings.</p>

                                <div className="claim-input-wrapper">
                                    <span className="prefix">u/</span>
                                    <input
                                        value={redditId}
                                        onChange={(e) => setRedditId(e.target.value)}
                                        placeholder="username"
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>

                                <button
                                    className="qinto-btn-large"
                                    onClick={handleInitialSearch}
                                    disabled={loading || !redditId}
                                >
                                    {loading ? "Searching..." : "Find My Ads"}
                                </button>
                            </motion.div>
                        )}

                        {/* --- STEP: SELECT --- */}
                        {step === STEPS.SELECT && (
                            <motion.div
                                key="select"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <h2>Select Ads to Keep</h2>
                                <p>Unselected items will be deleted permanently.</p>

                                <div className="ad-selection-list">
                                    {foundAds.map(ad => (
                                        <div
                                            key={ad.id}
                                            className={`selection-item ${selectedIds.includes(ad.id) ? 'active' : ''}`}
                                            onClick={() => toggleAdSelection(ad.id)}
                                        >
                                            <div className="checkbox-wrapper">
                                                {selectedIds.includes(ad.id) ?
                                                    <FiCheckCircle className="check-icon" /> :
                                                    <FiCircle className="uncheck-icon" />
                                                }
                                            </div>
                                            <img src={ad.photos[0]?.thumb || ad.photos[0]?.full} alt="" />
                                            <div className="item-meta">
                                                <h4>{ad.title}</h4>
                                                <span>₹{Number(ad.price).toLocaleString('en-IN')}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {foundAds.length - selectedIds.length > 0 && (
                                    <div className="security-notice">
                                        <FiAlertTriangle />
                                        <p>The {foundAds.length - selectedIds.length} ads you haven't selected will be erased from our servers immediately upon confirmation.</p>
                                    </div>
                                )}

                                <div className="action-row-flex">
                                    <button className="reset-btn" onClick={() => setStep(STEPS.SEARCH)}>Back</button>
                                    <button
                                        className="qinto-btn-large"
                                        onClick={handleFinalClaim}
                                        disabled={loading || selectedIds.length === 0}
                                    >
                                        {loading ? "Processing..." : `Claim ${selectedIds.length} Ads`}
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {/* --- STEP: SUCCESS --- */}
                        {step === STEPS.SUCCESS && (
                            <motion.div
                                key="success"
                                className="success-state"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                            >
                                <div className="success-icon-large">
                                    <FiCheckCircle />
                                </div>
                                <h2>Success!</h2>
                                <div className="stats-box">
                                    <div className="stat-item">
                                        <strong>{result?.claimed || 0}</strong>
                                        <span>Claimed</span>
                                    </div>
                                    <div className="stat-divider" />
                                    <div className="stat-item">
                                        <strong>{result?.deleted || 0}</strong>
                                        <span>Deleted</span>
                                    </div>
                                </div>
                                <p>Your selected items and their chats have been moved to your account.</p>
                                <button className="qinto-btn-large" onClick={() => navigate('/my-ads')}>
                                    Go to My Ads
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <Modal
            icon={<FaBuysellads/>}
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title="Confirm Claim"
                message={confirmMsg}
                buttons={[
                    { label: "Cancel", onClick: () => setShowModal(false) },
                    { label: "Confirm", onClick: executeClaim, className: "primary" }
                ]}
            />
        </div>
    );
};

export default ClaimAdsPage;